import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';

const emptyGoalForm = {
  id: null,
  name: '',
  description: '',
  target_amount: '',
  current_amount: '',
  contribution_percent: '',
  priority: 1,
  status: 'ACTIVE',
  starts_after_goal_id: '',
};

const formatMoney = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '$0.00';

  return `$${num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatPercent = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0%';
  return `${num.toFixed(1)}%`;
};

const cleanNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

export default function Goals() {
  const [dashboard, setDashboard] = useState(null);
  const [recentContributions, setRecentContributions] = useState([]);
  const [form, setForm] = useState(emptyGoalForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recording, setRecording] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [customContributionAmount, setCustomContributionAmount] = useState('');
  const [contributionNotes, setContributionNotes] = useState('');

  const goals = dashboard?.goals || [];
  const activeGoal = dashboard?.activeGoal || null;

  const completedGoals = useMemo(() => {
    return goals.filter((goal) => String(goal.status).toUpperCase() === 'COMPLETED');
  }, [goals]);

  const activeGoals = useMemo(() => {
    return goals.filter((goal) => String(goal.status).toUpperCase() === 'ACTIVE');
  }, [goals]);

  const pausedGoals = useMemo(() => {
    return goals.filter((goal) => String(goal.status).toUpperCase() === 'PAUSED');
  }, [goals]);

  const todayNetProfit = cleanNumber(dashboard?.todayNetProfit);
  const recommendedContribution = cleanNumber(dashboard?.recommendedContribution);
  const contributedToday = cleanNumber(dashboard?.contributedToday);

  const remainingAfterRecommended = Math.max(0, todayNetProfit - recommendedContribution);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      setError('');

      const res = await api.get('/goals/dashboard');
      setDashboard(res.data || null);
    } catch (err) {
      console.error('[Goals] dashboard error:', err.response?.data || err.message);
      setError(
        err.response?.data?.error ||
          err.response?.data?.details ||
          err.message ||
          'Failed to load goals dashboard.'
      );
    } finally {
      setLoading(false);
    }
  };

  const loadRecentContributions = async () => {
    try {
      const res = await api.get('/goals/contributions/recent');
      setRecentContributions(res.data?.contributions || []);
    } catch (err) {
      console.error('[Goals] contributions error:', err.response?.data || err.message);
    }
  };

  useEffect(() => {
    loadDashboard();
    loadRecentContributions();
  }, []);

  const updateForm = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const resetForm = () => {
    setForm(emptyGoalForm);
    setShowForm(false);
  };

  const startCreateGoal = () => {
    setForm({
      ...emptyGoalForm,
      priority: goals.length + 1,
      status: 'ACTIVE',
    });
    setShowForm(true);
  };

  const startEditGoal = (goal) => {
    setForm({
      id: goal.id,
      name: goal.name || '',
      description: goal.description || '',
      target_amount: goal.target_amount ?? '',
      current_amount: goal.current_amount ?? '',
      contribution_percent: goal.contribution_percent ?? '',
      priority: goal.priority ?? 1,
      status: goal.status || 'ACTIVE',
      starts_after_goal_id: goal.starts_after_goal_id || '',
    });

    setShowForm(true);

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const saveGoal = async () => {
    if (!form.name.trim()) {
      alert('Goal name is required.');
      return;
    }

    if (cleanNumber(form.target_amount) <= 0) {
      alert('Target amount must be greater than 0.');
      return;
    }

    try {
      setSaving(true);

      const payload = {
        name: form.name.trim(),
        description: form.description || null,
        target_amount: cleanNumber(form.target_amount),
        current_amount: cleanNumber(form.current_amount),
        contribution_percent: cleanNumber(form.contribution_percent),
        priority: Number(form.priority) || 1,
        status: form.status || 'ACTIVE',
        starts_after_goal_id: form.starts_after_goal_id || null,
      };

      if (form.id) {
        await api.put(`/goals/${form.id}`, payload);
      } else {
        await api.post('/goals', payload);
      }

      resetForm();
      await loadDashboard();
    } catch (err) {
      console.error('[Goals] save error:', err.response?.data || err.message);

      alert(
        err.response?.data?.error ||
          err.response?.data?.details ||
          err.message ||
          'Failed to save goal.'
      );
    } finally {
      setSaving(false);
    }
  };

  const deleteGoal = async (goal) => {
    if (!window.confirm(`Delete goal "${goal.name}"? This also deletes its contribution history.`)) {
      return;
    }

    try {
      await api.delete(`/goals/${goal.id}`);
      await loadDashboard();
      await loadRecentContributions();
    } catch (err) {
      console.error('[Goals] delete error:', err.response?.data || err.message);

      alert(
        err.response?.data?.error ||
          err.response?.data?.details ||
          err.message ||
          'Failed to delete goal.'
      );
    }
  };

  const recordRecommendedContribution = async () => {
    if (!activeGoal) {
      alert('No active goal found.');
      return;
    }

    const amount = customContributionAmount
      ? cleanNumber(customContributionAmount)
      : recommendedContribution;

    if (amount <= 0) {
      alert('Contribution amount must be greater than 0.');
      return;
    }

    const confirmMessage =
      `Record contribution?\n\n` +
      `Goal: ${activeGoal.name}\n` +
      `Today Net: ${formatMoney(todayNetProfit)}\n` +
      `Contribution: ${formatMoney(amount)}\n`;

    if (!window.confirm(confirmMessage)) return;

    try {
      setRecording(true);

      await api.post('/goals/contribute', {
        goal_id: activeGoal.id,
        source: 'shopify_profit',
        source_date: dashboard?.today,
        net_profit: todayNetProfit,
        contribution_percent: activeGoal.contribution_percent,
        contribution_amount: amount,
        notes: contributionNotes || null,
      });

      setCustomContributionAmount('');
      setContributionNotes('');

      await loadDashboard();
      await loadRecentContributions();
    } catch (err) {
      console.error('[Goals] contribution error:', err.response?.data || err.message);

      alert(
        err.response?.data?.error ||
          err.response?.data?.details ||
          err.message ||
          'Failed to record contribution.'
      );
    } finally {
      setRecording(false);
    }
  };

  const getProgressPercent = (goal) => {
    const target = cleanNumber(goal.target_amount);
    const current = cleanNumber(goal.current_amount);

    if (target <= 0) return 0;

    return Math.min(100, Math.max(0, (current / target) * 100));
  };

  const statusBadge = (status) => {
    const cleanStatus = String(status || '').toUpperCase();

    if (cleanStatus === 'COMPLETED') {
      return <span className="badge bg-success">Completed</span>;
    }

    if (cleanStatus === 'PAUSED') {
      return <span className="badge bg-secondary">Paused</span>;
    }

    return <span className="badge bg-primary">Active</span>;
  };

  const GoalCard = ({ goal, highlight = false }) => {
    const progress = getProgressPercent(goal);
    const remaining = Math.max(
      0,
      cleanNumber(goal.target_amount) - cleanNumber(goal.current_amount)
    );

    return (
      <div className="col-md-6 col-xl-4 mb-3">
        <div
          className={`card h-100 ${
            highlight ? 'border-warning shadow-sm' : ''
          }`}
        >
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-start mb-2">
              <div>
                <h5 className="mb-1">{goal.name}</h5>
                <div className="text-muted small">Priority #{goal.priority}</div>
              </div>

              {statusBadge(goal.status)}
            </div>

            {goal.description && (
              <p className="text-muted small mb-2">{goal.description}</p>
            )}

            {goal.starts_after_goal_name && (
              <div className="small text-muted mb-2">
                Starts after: <strong>{goal.starts_after_goal_name}</strong>
              </div>
            )}

            <div className="mb-2">
              <div className="d-flex justify-content-between">
                <strong>{formatMoney(goal.current_amount)}</strong>
                <span>{formatMoney(goal.target_amount)}</span>
              </div>

              <div className="progress" style={{ height: 12 }}>
                <div
                  className={`progress-bar ${
                    String(goal.status).toUpperCase() === 'COMPLETED'
                      ? 'bg-success'
                      : 'bg-warning'
                  }`}
                  role="progressbar"
                  style={{ width: `${progress}%` }}
                  aria-valuenow={progress}
                  aria-valuemin="0"
                  aria-valuemax="100"
                />
              </div>

              <div className="d-flex justify-content-between mt-1">
                <small>{formatPercent(progress)}</small>
                <small>Remaining: {formatMoney(remaining)}</small>
              </div>
            </div>

            <div className="border rounded p-2 mb-3">
              <small className="d-block">
                Put-back rule:{' '}
                <strong>{formatPercent(goal.contribution_percent)} of net</strong>
              </small>
            </div>

            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => startEditGoal(goal)}
              >
                Edit
              </button>

              <button
                className="btn btn-sm btn-outline-danger"
                onClick={() => deleteGoal(goal)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="container-fluid mt-4">
        <h2>Goals</h2>
        <p>Loading goals...</p>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h2 className="mb-1">Goals</h2>
          <p className="text-muted mb-0">
            Track business goals, daily put-back rules, and long-term upgrades.
          </p>
        </div>

        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary" onClick={loadDashboard}>
            Refresh
          </button>

          <button className="btn btn-success" onClick={startCreateGoal}>
            + New Goal
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">
          {error}{' '}
          <button className="btn btn-sm btn-light ms-2" onClick={loadDashboard}>
            Retry
          </button>
        </div>
      )}

      {showForm && (
        <div className="card p-3 mb-4 shadow-sm">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="mb-0">{form.id ? 'Edit Goal' : 'Create Goal'}</h4>

            <button className="btn btn-sm btn-outline-secondary" onClick={resetForm}>
              Cancel
            </button>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">Goal Name</label>
              <input
                className="form-control"
                value={form.name}
                onChange={(e) => updateForm('name', e.target.value)}
                placeholder="Emergency Fund"
              />
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">Target Amount</label>
              <input
                className="form-control"
                value={form.target_amount}
                onChange={(e) => updateForm('target_amount', e.target.value)}
                placeholder="10000"
              />
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">Current Amount</label>
              <input
                className="form-control"
                value={form.current_amount}
                onChange={(e) => updateForm('current_amount', e.target.value)}
                placeholder="0"
              />
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">Put-Back %</label>
              <input
                className="form-control"
                value={form.contribution_percent}
                onChange={(e) => updateForm('contribution_percent', e.target.value)}
                placeholder="80"
              />
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">Priority</label>
              <input
                className="form-control"
                value={form.priority}
                onChange={(e) => updateForm('priority', e.target.value)}
                placeholder="1"
              />
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={form.status}
                onChange={(e) => updateForm('status', e.target.value)}
              >
                <option value="ACTIVE">Active</option>
                <option value="PAUSED">Paused</option>
                <option value="COMPLETED">Completed</option>
              </select>
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">Starts After</label>
              <select
                className="form-select"
                value={form.starts_after_goal_id}
                onChange={(e) => updateForm('starts_after_goal_id', e.target.value)}
              >
                <option value="">No dependency</option>

                {goals
                  .filter((goal) => String(goal.id) !== String(form.id))
                  .map((goal) => (
                    <option key={goal.id} value={goal.id}>
                      {goal.name}
                    </option>
                  ))}
              </select>
            </div>

            <div className="col-12 mb-3">
              <label className="form-label">Description</label>
              <textarea
                className="form-control"
                rows={3}
                value={form.description}
                onChange={(e) => updateForm('description', e.target.value)}
                placeholder="Example: Build emergency fund before funding other garage upgrades."
              />
            </div>
          </div>

          <button className="btn btn-primary" onClick={saveGoal} disabled={saving}>
            {saving ? 'Saving...' : form.id ? 'Save Goal' : 'Create Goal'}
          </button>
        </div>
      )}

      <div className="row mb-4">
        <div className="col-md-3 mb-3">
          <div className="card p-3 h-100">
            <small className="text-muted">Today Shopify Net</small>
            <h3 className="mb-0">{formatMoney(todayNetProfit)}</h3>
          </div>
        </div>

        <div className="col-md-3 mb-3">
          <div className="card p-3 h-100 border-warning">
            <small className="text-muted">Recommended Put-Back</small>
            <h3 className="mb-0">{formatMoney(recommendedContribution)}</h3>
          </div>
        </div>

        <div className="col-md-3 mb-3">
          <div className="card p-3 h-100">
            <small className="text-muted">After Put-Back</small>
            <h3 className="mb-0">{formatMoney(remainingAfterRecommended)}</h3>
          </div>
        </div>

        <div className="col-md-3 mb-3">
          <div className="card p-3 h-100">
            <small className="text-muted">Recorded Today</small>
            <h3 className="mb-0">{formatMoney(contributedToday)}</h3>
          </div>
        </div>
      </div>

      <div className="card p-3 mb-4 shadow-sm">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <h4 className="mb-1">Daily Goal Put-Back</h4>
            <p className="text-muted mb-0">
              Uses today’s Shopify net profit and the active goal’s put-back percentage.
            </p>
          </div>

          {activeGoal ? (
            <span className="badge bg-warning text-dark">Funding: {activeGoal.name}</span>
          ) : (
            <span className="badge bg-secondary">No active goal</span>
          )}
        </div>

        {activeGoal ? (
          <div className="row align-items-end">
            <div className="col-md-4 mb-3">
              <label className="form-label">Custom Contribution Amount</label>
              <input
                className="form-control"
                value={customContributionAmount}
                onChange={(e) => setCustomContributionAmount(e.target.value)}
                placeholder={recommendedContribution.toFixed(2)}
              />
              <small className="text-muted">
                Leave blank to use recommended amount.
              </small>
            </div>

            <div className="col-md-5 mb-3">
              <label className="form-label">Notes</label>
              <input
                className="form-control"
                value={contributionNotes}
                onChange={(e) => setContributionNotes(e.target.value)}
                placeholder="Example: Shopify daily put-back"
              />
            </div>

            <div className="col-md-3 mb-3">
              <button
                className="btn btn-warning w-100"
                onClick={recordRecommendedContribution}
                disabled={recording || recommendedContribution <= 0}
              >
                {recording ? 'Recording...' : 'Record Put-Back'}
              </button>
            </div>
          </div>
        ) : (
          <div className="alert alert-secondary mb-0">
            Create an active goal or check that previous dependent goals are completed.
          </div>
        )}
      </div>

      {activeGoal && (
        <div className="mb-4">
          <h4>Active Goal</h4>
          <div className="row">
            <GoalCard goal={activeGoal} highlight />
          </div>
        </div>
      )}

      <div className="mb-4">
        <h4>Active Goals</h4>

        {activeGoals.length === 0 ? (
          <div className="alert alert-secondary">No active goals yet.</div>
        ) : (
          <div className="row">
            {activeGoals.map((goal) => (
              <GoalCard key={goal.id} goal={goal} />
            ))}
          </div>
        )}
      </div>

      {pausedGoals.length > 0 && (
        <div className="mb-4">
          <h4>Paused Goals</h4>

          <div className="row">
            {pausedGoals.map((goal) => (
              <GoalCard key={goal.id} goal={goal} />
            ))}
          </div>
        </div>
      )}

      {completedGoals.length > 0 && (
        <div className="mb-4">
          <h4>Completed Goals</h4>

          <div className="row">
            {completedGoals.map((goal) => (
              <GoalCard key={goal.id} goal={goal} />
            ))}
          </div>
        </div>
      )}

      <div className="card p-3 mb-4">
        <h4>Recent Contributions</h4>

        {recentContributions.length === 0 ? (
          <div className="text-muted">No contributions recorded yet.</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm table-striped align-middle">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Goal</th>
                  <th>Source</th>
                  <th>Net Profit</th>
                  <th>Percent</th>
                  <th>Contribution</th>
                  <th>Notes</th>
                </tr>
              </thead>

              <tbody>
                {recentContributions.map((item) => (
                  <tr key={item.id}>
                    <td>{item.source_date}</td>
                    <td>{item.goal_name}</td>
                    <td>{item.source}</td>
                    <td>{formatMoney(item.net_profit)}</td>
                    <td>{formatPercent(item.contribution_percent)}</td>
                    <td>
                      <strong>{formatMoney(item.contribution_amount)}</strong>
                    </td>
                    <td>{item.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}